import React, { useEffect, useState } from "react";
import {
  BlueBirdTheme, Divider, Dropdown, Radio, RadioGroup, Typography,
} from "@kitbird/core";
import { Controller } from "react-hook-form";
import styled from "styled-components";
import useCreateReceiptSubmission from "../hooks/useCreateReceiptSubmission";
import { FormGroup, FormWrapper } from "../../../../components/Form/Form.style";
import validationRules from "../../../../utils/validation";
import { ThemeBackground } from "../../../../components/Content/Content.style";
import avatarImg from "../../../../assets/images/avatar.svg";
import useAuth from "../../../../components/Authentication/useAuth";
import { PositionInfoI } from "../../../../services/userAccountService";
import { listCodeTypeAll } from "../constants";
import { ReactComponent as AccountIcon } from "../../../../assets/icons/account.svg";

interface DetailApplicantFormI {
  useCustomHook: ReturnType<typeof useCreateReceiptSubmission>
}

const { danger, gray, primary } = BlueBirdTheme.colors;

const InputRadio = styled.div`
  padding: 0 12px;
  align-self: center;
`;

const WrapperBox = styled.div<{ $isSelect: boolean }>`
  display: flex;
  min-width: 280px;
  margin-bottom: 12px;
  box-sizing: border-box;
  cursor: pointer;

  border: 1px ${({ $isSelect }) => ($isSelect ? primary[500] : gray.line)} solid;
  background-color: ${({ $isSelect }) => ($isSelect ? primary[100] : "transparent")};
  border-radius: 6px;
  padding: 10px 12px;
`;

const CustomRadioGroup = styled(RadioGroup)`
  overflow-x: auto;
  width: 100%;

  > div {
    gap: 16px;
  }

  div > div:hover {
    background-color: ${primary[100]};
  }
`;

const CustomThemeBackground = styled(ThemeBackground)`
  display: flex;
  gap: 24px;
  align-items: center;
  background-color: ${primary[100]};
  border-radius: 12px;
  padding: 16px 24px;
`;

const RoundWrapper = styled.div`
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: ${primary[200]};
  align-content: center;
  text-align-last: center;
`;

const OptionLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px;
`;

const DetailApplicantForm = ({ useCustomHook }: DetailApplicantFormI) => {
  const { userAccount } = useAuth();
  const {
    dataUser,
    methods,
    companyOpts,
    companyRelationOpts,
  } = useCustomHook;
  const {
    control, resetField, setValue, formState: { errors },
  } = methods;
  const [selected, setSelected] = useState<any>();

  const selectUserPosition = (position: PositionInfoI) => {
    setSelected(position);
    setValue("codeType", { label: position.code_type, value: position.code_type }, { shouldValidate: true });
    setValue("area", { label: position.area_name, value: position.area_id }, { shouldValidate: true });
    setValue("pool", { label: position.pool_name, value: position.pool_id }, { shouldValidate: true });
    setValue("segment", { label: position.segments_info.desc_segment, value: position.segments_info.code_segment }, { shouldValidate: true });
    setValue("department", position.department, { shouldValidate: true });
    resetField("company");
    resetField("companyRelation");
  };

  useEffect(() => {
    if (userAccount.position_info.length === 1) selectUserPosition(userAccount.position_info[0]);
  }, [userAccount.position_info]);

  return (
    <>
      <Typography level={6} color={gray.title} fontWeight="medium">Pemohon</Typography>
      <CustomThemeBackground>
        <img src={avatarImg} width={80} height={80} />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Typography level={4} fontWeight="medium" color={gray.subtitle}>{dataUser.employee_name}</Typography>
          <Typography level={6} fontWeight="medium" color={gray.subtitle}>{dataUser.employee_id}</Typography>
        </div>
      </CustomThemeBackground>
      <Typography level={6} color={gray.title} fontWeight="medium">Jabatan terkait</Typography>
      <div>
        <CustomRadioGroup direction="horizontal" value={selected?.id}>
          {userAccount.position_info.map((item) => (
            <WrapperBox
              $isSelect={item.id === selected?.id}
              key={item.id}
              onClick={() => selectUserPosition(item)}
            >
              {userAccount.position_info.length > 1 && <InputRadio>
                <Radio
                  value={item.id}
                  name='detail-applicant'
                  checked={item.id === selected?.id}
                />
              </InputRadio>}
              <div style={{ width: "100%" }}>
                <OptionLabel>
                  <div>
                    <Typography level={5} fontWeight="medium">
                      {listCodeTypeAll.find(
                        (opt) => opt.code_type === item.code_type,
                      )?.code_type_desc}
                      &nbsp;•&nbsp;
                      {item.pool_name} ({item.pool_id})
                    </Typography>
                    <br />
                    <Typography level={7}>{item.segments_info.desc_segment}</Typography>
                  </div>
                  <Divider style={{ border: `1px dashed ${gray.line}` }} />
                  <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <RoundWrapper>
                      <AccountIcon />
                    </RoundWrapper>
                    <div>
                      <Typography level={5} fontWeight="medium">{item.job_title}</Typography>
                      <br />
                      <Typography level={7}>{item.department}</Typography>
                    </div>
                  </div>
                </OptionLabel>
              </div>
            </WrapperBox>
          ))}
        </CustomRadioGroup >
        {(errors.codeType || errors.department)
          && <Typography level={7} color={danger[500]}>Harap memilih jabatan</Typography>
        }
      </div>

      <Typography level={6} color={gray.title} fontWeight="medium">Perusahaan terkait</Typography>
      <FormGroup>
        <FormWrapper>
          <Controller
            control={control}
            name="company"
            rules={{
              required: validationRules.defaultRequired,
            }}
            render={({ field, fieldState: { error } }) => (
              <Dropdown
                id={field.name}
                name={field.name}
                label="Perusahaan"
                onSelected={(opt) => {
                  setValue("company", opt, { shouldValidate: true });
                  resetField("companyRelation");
                }}
                options={companyOpts}
                disabled={companyOpts.length <= 1}
                value={field.value?.label}
                error={
                  error && {
                    message: (error?.message || errors.company?.value?.message) || "",
                  }
                }
              />
            )}
          />
        </FormWrapper>
        <FormWrapper>
          <Controller
            control={control}
            name="companyRelation"
            rules={{
              required: validationRules.defaultRequired,
            }}
            render={({ field, fieldState: { error } }) => (
              <Dropdown
                id={field.name}
                name={field.name}
                label="Perusahaan relasi"
                onSelected={(opt) => setValue("companyRelation", opt)}
                options={companyRelationOpts}
                disabled={companyRelationOpts.length <= 1}
                value={field.value?.label}
                error={
                  error && {
                    message: error?.message || "",
                  }
                }
              />
            )}
          />
        </FormWrapper>
      </FormGroup>
    </>
  );
};

export default DetailApplicantForm;
